/** Element shapes match what parseStructurizrDsl emits. */
export interface RectMeta {
  name: string;
  tech: string;
  desc: string;
}

/**
 * Extract Name / Technology / Description from the text element attached to a rectangle.
 * `parseStructurizrDsl` writes a 3-line content via `buildC4Text`:
 *   line 0: name
 *   line 1: "[Type]" or "[Type: Tech]"  (optional)
 *   line 2: description                 (optional)
 */
export function extractRectMeta(elements: any[], rectId: string): RectMeta {
  const text = elements.find(
    (e) => e?.type === 'text' && e.attachedToElement === rectId,
  );
  if (!text || typeof text.content !== 'string') {
    return { name: '', tech: '', desc: '' };
  }
  const [name = '', line1 = '', desc = ''] = text.content.split('\n');
  const techMatch = line1.match(/\[(?:[^:\]]+):\s*([^\]]+)\]/);
  return {
    name: name.trim(),
    tech: techMatch ? techMatch[1].trim() : '',
    desc: desc.trim(),
  };
}

/**
 * elementMap entry from parseStructurizrDsl: { varName, fullPath, kind, label }.
 */
export interface ElementMapEntry {
  varName: string;
  fullPath: string;
  kind: string;
  label: string;
}

/**
 * Stable key for an arrow across versions: "<srcVarName>::<tgtVarName>".
 * Returns null if either endpoint is not in the elementMap.
 */
export function arrowKey(arrow: any, elementMap: Map<string, ElementMapEntry>): string | null {
  const srcId = arrow?.startConnection?.elementId;
  const tgtId = arrow?.endConnection?.elementId;
  const src = srcId ? elementMap.get(srcId) : undefined;
  const tgt = tgtId ? elementMap.get(tgtId) : undefined;
  if (!src || !tgt) return null;
  return `${src.varName}::${tgt.varName}`;
}

/**
 * Stable hash of an arrow: endpoints (by varName) + label text(s) attached to the arrow.
 */
export function fingerprintArrow(
  elements: any[],
  arrow: any,
  elementMap: Map<string, ElementMapEntry>,
): string {
  const labels = elements
    .filter((e) => e?.type === 'text' && e.attachedToArrow === arrow?.id)
    .map((e) => String(e.content ?? ''))
    .sort();
  return JSON.stringify({
    key: arrowKey(arrow, elementMap),
    labels,
  });
}

export interface ParsedDsl {
  elements: any[];
  elementMap: Map<string, ElementMapEntry>;
}

export interface C4DiffSets {
  /** Rectangle varNames present only in `to`. */
  added: Set<string>;
  /** Rectangle varNames present only in `from`. */
  removed: Set<string>;
  /** Rectangle varNames in both versions with differing fingerprints. */
  changed: Set<string>;
  /** Arrow keys (srcVar::tgtVar) present only in `to`. */
  addedArrows: Set<string>;
  /** Arrow keys present only in `from`. */
  removedArrows: Set<string>;
  /** Arrow keys in both versions with differing fingerprints. */
  changedArrows: Set<string>;
}

/** Build a varName → rect lookup once (faster than scanning elementMap repeatedly). */
function buildVarToRect(parsed: ParsedDsl): Map<string, any> {
  const out = new Map<string, any>();
  for (const [id, meta] of parsed.elementMap) {
    const rect = parsed.elements.find((e) => e.id === id && e.type === 'rectangle');
    if (rect) out.set(meta.varName, rect);
  }
  return out;
}

export function computeC4Diff(from: ParsedDsl, to: ParsedDsl): C4DiffSets {
  const added = new Set<string>();
  const removed = new Set<string>();
  const changed = new Set<string>();
  const addedArrows = new Set<string>();
  const removedArrows = new Set<string>();
  const changedArrows = new Set<string>();

  const fromVarRect = buildVarToRect(from);
  const toVarRect = buildVarToRect(to);

  // Rectangles
  for (const [varName, toRect] of toVarRect) {
    const fromRect = fromVarRect.get(varName);
    if (!fromRect) {
      added.add(varName);
      continue;
    }
    const fpFrom = fingerprintRectangle(from.elements, fromRect);
    const fpTo = fingerprintRectangle(to.elements, toRect);
    if (fpFrom !== fpTo) changed.add(varName);
  }
  for (const varName of fromVarRect.keys()) {
    if (!toVarRect.has(varName)) removed.add(varName);
  }

  // Arrows
  const fromArrows = new Map<string, any>();
  const toArrows = new Map<string, any>();
  for (const e of from.elements) {
    if (e?.type !== 'arrow') continue;
    const k = arrowKey(e, from.elementMap);
    if (k) fromArrows.set(k, e);
  }
  for (const e of to.elements) {
    if (e?.type !== 'arrow') continue;
    const k = arrowKey(e, to.elementMap);
    if (k) toArrows.set(k, e);
  }

  for (const [k, toArrow] of toArrows) {
    if (!fromArrows.has(k)) {
      addedArrows.add(k);
      continue;
    }
    const fpFrom = fingerprintArrow(from.elements, fromArrows.get(k), from.elementMap);
    const fpTo = fingerprintArrow(to.elements, toArrow, to.elementMap);
    if (fpFrom !== fpTo) changedArrows.add(k);
  }
  for (const k of fromArrows.keys()) {
    if (!toArrows.has(k)) removedArrows.add(k);
  }

  return { added, removed, changed, addedArrows, removedArrows, changedArrows };
}

/**
 * Stable hash of a rectangle's semantic content.
 * Excludes geometry (x/y/w/h), generated ids (id, c4.contextId, attachedToElement),
 * so re-parsing the same DSL always yields the same fingerprint.
 */
export function fingerprintRectangle(elements: any[], rect: any): string {
  if (!rect) return '';
  const meta = extractRectMeta(elements, rect.id);
  return JSON.stringify({
    kind: rect?.c4?.kind ?? null,
    name: meta.name,
    tech: meta.tech,
    desc: meta.desc,
    fill: rect.fill ?? null,
    stroke: rect.stroke ?? null,
  });
}

/**
 * For each rectangle, count how many descendants are diff entries
 * (added | removed | changed | added arrow endpoint | changed arrow endpoint).
 *
 * Keys in the returned map are rectIds from the `to` ParsedDsl.
 * Walks up the `c4.contextId` chain from each diff-touched element.
 */
export function aggregateSubtreeCounts(to: ParsedDsl, diff: C4DiffSets): Map<string, number> {
  const counts = new Map<string, number>();
  const rectById = new Map<string, any>();
  for (const e of to.elements) {
    if (e?.type === 'rectangle') rectById.set(e.id, e);
  }

  const bump = (rectId: string | null | undefined) => {
    let cur = rectId;
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      counts.set(cur, (counts.get(cur) ?? 0) + 1);
      const parentCtx = rectById.get(cur)?.c4?.contextId ?? null;
      cur = parentCtx;
    }
  };

  // Reverse varName → rectId in the `to` version.
  const toVarToId = new Map<string, string>();
  for (const [id, meta] of to.elementMap) toVarToId.set(meta.varName, id);

  // Added rectangles: walk up from the new rect's parent context.
  for (const varName of diff.added) {
    const rectId = toVarToId.get(varName);
    const parentCtx = rectId ? rectById.get(rectId)?.c4?.contextId ?? null : null;
    bump(parentCtx);
  }
  // Changed rectangles: walk up from the rect's parent context.
  for (const varName of diff.changed) {
    const rectId = toVarToId.get(varName);
    const parentCtx = rectId ? rectById.get(rectId)?.c4?.contextId ?? null : null;
    bump(parentCtx);
  }

  // Changed arrows: walk up from the arrow's source rect (in `to`).
  for (const k of diff.changedArrows) {
    const [srcVar] = k.split('::');
    const rectId = toVarToId.get(srcVar);
    const parentCtx = rectId ? rectById.get(rectId)?.c4?.contextId ?? null : null;
    bump(parentCtx);
  }
  for (const k of diff.addedArrows) {
    const [srcVar] = k.split('::');
    const rectId = toVarToId.get(srcVar);
    const parentCtx = rectId ? rectById.get(rectId)?.c4?.contextId ?? null : null;
    bump(parentCtx);
  }

  return counts;
}

export interface BreadcrumbSegment {
  /** null for the root segment (Context level). */
  id: string | null;
  label: string;
}

/**
 * Walk up the c4.contextId chain from `currentContextId` and produce labels
 * suitable for breadcrumb display. Always starts with root segment "System Context".
 */
export function buildBreadcrumbPath(
  parsed: ParsedDsl,
  currentContextId: string | null,
): BreadcrumbSegment[] {
  const segments: BreadcrumbSegment[] = [{ id: null, label: 'System Context' }];
  if (!currentContextId) return segments;

  const rectById = new Map<string, any>();
  for (const e of parsed.elements) {
    if (e?.type === 'rectangle') rectById.set(e.id, e);
  }

  // Collect ancestors then reverse so root-most comes first.
  const chain: string[] = [];
  let cur: string | null = currentContextId;
  const seen = new Set<string>();
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    chain.push(cur);
    cur = rectById.get(cur)?.c4?.contextId ?? null;
  }
  chain.reverse();

  for (const rectId of chain) {
    const meta = parsed.elementMap.get(rectId);
    const label = meta?.label || extractRectMeta(parsed.elements, rectId).name || '?';
    segments.push({ id: rectId, label });
  }
  return segments;
}

/**
 * Translate a contextId rectId from one ParsedDsl to its varName-equivalent
 * rectId in the other. Returns null for the root context (which is shared).
 * Returns undefined when no matching varName exists in the target.
 */
export function mapContextIdAcrossVersions(
  contextId: string | null,
  source: ParsedDsl,
  target: ParsedDsl,
): string | null | undefined {
  if (contextId === null) return null;
  const sourceMeta = source.elementMap.get(contextId);
  if (!sourceMeta) return undefined;
  for (const [id, meta] of target.elementMap) {
    if (meta.varName === sourceMeta.varName) return id;
  }
  return undefined;
}
